import { FC } from 'react'
import {
  addDisplayName,
  flowMax,
  addProps,
  addHandlers,
  addDefaultProps,
} from 'ad-hok'
import { createUseStyles } from 'react-jss'
import { addOptionalIntPathParam } from 'shared/src/utils/addNavigate'
import {
  ProductDiscoverySource,
  ProductWithImage,
  UserWithImages,
} from 'shared/src/api/types'
import cx from 'classnames'

import { Product, HEIGHT as PRODUCT_HEIGHT } from './Product'
import { animationDuration, animationTimingFunction } from './animation'
import { Lasso as BaseLasso } from '../../../Lasso'
import { makeProductDetailUrl } from '../../../../utils/routes'
import { ShuffleButton } from './ShuffleButton'
import {
  FOOTER_BOTTOM_OFFSET,
  TABS_HEIGHT,
  TABS_TOP,
} from 'components/feed/layout'

export const PRODUCTS_PER_LASSO = 3

const MAX_LASSO_HEIGHT = `calc(100% - ${TABS_TOP}px - ${TABS_HEIGHT}px - ${PRODUCT_HEIGHT}px - ${FOOTER_BOTTOM_OFFSET}px + 20px)`

type AnimationDirection = 'normal' | 'reverse'

interface StyleProps {
  isFlipped: boolean
}

const position =
  (top: number, left: number) =>
  ({ isFlipped }: StyleProps) => {
    const horizontal = `${left}%`
    const transform = isFlipped
      ? 'translate(50%, -50%)'
      : 'translate(-50%, -50%)'

    return {
      top: `${top}%`,
      left: isFlipped ? undefined : horizontal,
      right: isFlipped ? horizontal : undefined,
      transform,
    }
  }

const positionFromLeft = (top: number, left: number) => ({
  top: `${top}%`,
  left: `${left}%`,
  transform: 'translate(-50%, -50%)',
})

const positionFromRight = (top: number, right: number) => ({
  top: `${top}%`,
  right: `${right}%`,
  transform: 'translate(50%, -50%)',
})

const useStyles = createUseStyles({
  mediaProduct0: ({ isFlipped }: StyleProps) =>
    isFlipped ? positionFromRight(0, 25) : positionFromLeft(0, 30),
  mediaProduct1: ({ isFlipped }: StyleProps) =>
    isFlipped ? positionFromRight(25, 80) : positionFromLeft(45, 80),
  mediaProduct2: ({ isFlipped }: StyleProps) =>
    isFlipped ? positionFromRight(90, 45) : positionFromLeft(85, 25),

  relatedProduct0: position(0, 30),
  relatedProduct1: position(20, 76),
  relatedProduct2: position(80, 43),

  roundupProduct0: position(-10, 80),
  roundupProduct1: position(70, 20),
  roundupProduct2: position(100, 90),

  shufflePosition: ({ isFlipped }: StyleProps) =>
    isFlipped ? positionFromLeft(80, 22) : positionFromLeft(85, 64),

  relatedProductShufflePosition: position(73, 72),
  shuffle: {
    position: 'absolute',
    pointerEvents: 'auto',
  },
})

type Layout = 'media' | 'relatedProducts' | 'roundup'

interface Origin {
  user: Pick<UserWithImages, 'id' | 'username'>
  mediaId: number
}

interface Shuffle {
  onClick: () => void
  value?: string
  disabled?: boolean
}

interface Props {
  origin?: Origin | null
  products: ProductWithImage[]
  layout: Layout
  animate?: boolean
  animationDirection?: AnimationDirection
  className?: string
  showBookmarks?: boolean
  showPrices?: boolean
  isFlipped?: boolean
  shuffle?: Shuffle
  discoverySource?: ProductDiscoverySource
}

export const Lasso: FC<Props> = flowMax(
  addDisplayName('Lasso'),
  addDefaultProps({
    isFlipped: false,
  }),
  addOptionalIntPathParam('mediaId'),
  addHandlers({
    getProductDetailPath:
      ({ origin }) =>
      (product: ProductWithImage): string =>
        makeProductDetailUrl(
          product.id,
          origin
            ? {
                mediaId: origin.mediaId,
                creatorUsername: origin.user.username!,
              }
            : undefined
        ),
  }),
  addProps(({ isFlipped }) => ({
    classes: useStyles({
      isFlipped,
    }),
  })),
  addProps(({ layout, classes }) => ({
    childClasses:
      layout === 'media'
        ? [classes.mediaProduct0, classes.mediaProduct1, classes.mediaProduct2]
        : layout === 'relatedProducts'
        ? [
            classes.relatedProduct0,
            classes.relatedProduct1,
            classes.relatedProduct2,
          ]
        : [
            classes.roundupProduct0,
            classes.roundupProduct1,
            classes.roundupProduct2,
          ],
  })),
  addProps(({ mediaId, origin }) => ({
    mediaId: origin?.mediaId || mediaId,
    creatorId: origin?.user?.id,
  })),
  ({
    mediaId,
    creatorId,
    layout,
    products,
    getProductDetailPath,
    animate,
    animationDirection,
    isFlipped,
    shuffle,
    childClasses,
    className,
    classes,
    showBookmarks,
    showPrices,
    discoverySource,
  }) => (
    <BaseLasso
      layout={layout === 'roundup' ? 'roundup' : 'rodeo1'}
      isFlipped={isFlipped}
      animate={animate ?? false}
      animationDirection={animationDirection}
      animationDuration={animationDuration}
      animationTimingFunction={animationTimingFunction}
      button={
        shuffle && (
          <ShuffleButton
            value={shuffle.value}
            disabled={shuffle.disabled}
            onClick={shuffle.onClick}
            className={cx(
              layout === 'relatedProducts'
                ? classes.relatedProductShufflePosition
                : classes.shufflePosition,
              classes.shuffle
            )}
          />
        )
      }
      className={className}
      maxLassoHeight={MAX_LASSO_HEIGHT}
      childClasses={childClasses}
    >
      {products[0] && (
        <Product
          product={products[0]}
          navigateTo={getProductDetailPath(products[0])}
          showBookmark={showBookmarks}
          showPrice={showPrices}
          mediaId={mediaId}
          creatorId={creatorId}
          discoverySource={discoverySource}
        />
      )}
      {products[1] && (
        <Product
          product={products[1]}
          navigateTo={getProductDetailPath(products[1])}
          showBookmark={showBookmarks}
          showPrice={showPrices}
          mediaId={mediaId}
          creatorId={creatorId}
          discoverySource={discoverySource}
        />
      )}
      {products[2] && (
        <Product
          product={products[2]}
          navigateTo={getProductDetailPath(products[2])}
          showBookmark={showBookmarks}
          showPrice={showPrices}
          mediaId={mediaId}
          creatorId={creatorId}
          discoverySource={discoverySource}
        />
      )}
    </BaseLasso>
  )
)
